import { create } from "zustand";
import { applyBrandPack, type BrandPackId } from "@/theme/brandPacks";
import type { LocaleCode } from "@/i18n/messages";
import { translate, type MessageKey, type TranslateParams } from "@/i18n";

type UiState = {
  sidebarCollapsed: boolean;
  darkMode: boolean;
  locale: LocaleCode;
  brandPack: BrandPackId;
  toggleSidebar: () => void;
  setDarkMode: (on: boolean) => void;
  setLocale: (locale: LocaleCode) => void;
  setBrandPack: (pack: BrandPackId) => void;
  t: (key: MessageKey | string, params?: TranslateParams) => string;
};

function applyDarkClass(on: boolean) {
  document.documentElement.classList.toggle("dark", on);
}

function applyLocale(locale: LocaleCode) {
  document.documentElement.lang = locale;
}

/** v2 key: reset leftover dark preference from before class-based theme fix. Default = light. */
const DARK_MODE_KEY = "amos_dark_mode_v2";
const legacyDark = localStorage.getItem("amos_dark_mode");
if (legacyDark !== null) localStorage.removeItem("amos_dark_mode");

const savedDark = localStorage.getItem(DARK_MODE_KEY) === "1";
const savedLocale = (localStorage.getItem("amos_locale") || "en") as LocaleCode;
const savedBrand = (localStorage.getItem("amos_brand_pack") || "classic") as BrandPackId;

applyDarkClass(savedDark);
applyLocale(["en", "es", "hi"].includes(savedLocale) ? savedLocale : "en");
applyBrandPack(["classic", "slate", "teal"].includes(savedBrand) ? savedBrand : "classic");

export const useUiStore = create<UiState>((set, get) => ({
  sidebarCollapsed: false,
  darkMode: savedDark,
  locale: ["en", "es", "hi"].includes(savedLocale) ? savedLocale : "en",
  brandPack: ["classic", "slate", "teal"].includes(savedBrand) ? savedBrand : "classic",
  toggleSidebar: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
  setDarkMode: (on) => {
    localStorage.setItem(DARK_MODE_KEY, on ? "1" : "0");
    applyDarkClass(on);
    set({ darkMode: on });
  },
  setLocale: (locale) => {
    localStorage.setItem("amos_locale", locale);
    applyLocale(locale);
    set({ locale });
  },
  setBrandPack: (pack) => {
    localStorage.setItem("amos_brand_pack", pack);
    applyBrandPack(pack);
    set({ brandPack: pack });
  },
  t: (key, params) => translate(get().locale, key, params),
}));
